// We'll handle full page loads based on paths, as well as monitoring run-time
// additions to the DOM content using a mutationObserver.
//
// It's gonna get WEIRD. (Update: It got weird.)

import { addInitialFunction, Context } from "~/public_html/dfm5";

/** The target for any links about viewing/managing a school's subscription */
const subscriptionRoute = "/subscription";

/** Inline SVG for the padlock icon, for inline insertion (to respect currentColor) */
export const limitedBlockerIcon = `<svg class="limited-blocker-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>`;

/** Inline SVG for the "features" check marks, for inline insertion (to respect currentColor) */
const checkSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20 6 9 17l-5-5"/></svg>`;

/** Inline SVG for the badge-check icon used in the subscribed column */
const badgeCheckSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></svg>`;

/**
 * Does the full-page-blocking work based on the set of rules defined in blockedPages. Is run on
 * initial page load, but also when we detect a "navigation" via the mutation observer.
 */
const loadAction = () => {
  removeBlocker();
  const lookup = blockedPages[(new URL(window.location).pathname)];
  if (typeof lookup === "string") {
    // Use `addInitialFunction` to wait for the Context object to be populated with school.
    addInitialFunction(() => renderBlocker({ isDismissible: false, message: lookup }));
    return;
  } else if (typeof lookup === "function") {
    const result = lookup(window.location);
    if (result) {
      // Use `addInitialFunction` to wait for the Context object to be populated with school.
      addInitialFunction(() => renderBlocker({ isDismissible: false, message: result }));
      return;
    }
  }
}

/**
 * Taken from Fletcher code.
 *
 * The yearly costs of the subscription, excluding VAT, in GBP.
 *
 * These are hard-coded to avoid repeatedly fetching from Stripe.
 * As such, they should be updated to correspond with `STRIPE_UK_PRICE_ID` and `STRIPE_INTERNATIONAL_PRICE_ID`.
 */
const SUBSCRIPTION_COST_UK = 650;
const SUBSCRIPTION_COST_INTERNATIONAL = 750;

/**
 * Taken and adapted from Fletcher code.
 *
 * Returns the base yearly costs of the subscription, excluding VAT, in GBP for a given school.
 *
 * Note that this does not include any discounts, credit notes or anything else that the school may have accrued; the
 * indirection here is simply to determine whether the UK price or the International price would apply to this school.
 *
 * @param school The school for which to determine yearly "sticker price"
 */
function getBaseSubscriptionCost(school) {
  if (typeof school?.country !== "string") { return SUBSCRIPTION_COST_INTERNATIONAL };
  return school.country.startsWith("GB") ? SUBSCRIPTION_COST_UK : SUBSCRIPTION_COST_INTERNATIONAL;
}

/**
 * Generates and displays the HTML structure, to show when either a whole page
 * is being blocked on load, or a particular in-page action is being blocked.
 *
 * We'll follow a basic newspaper model of blocking interaction, and displaying
 * our message over the top. Not undefeatable, but sufficient for our purposes.
 *
 * CSS body:has() does much of the work -- we just add the elements to the DOM.
 *
 * Yes, we're back to string concatenation. Fight me.
 *
 * @param {boolean} isDismissible Whether the user can dismiss the blocker
 * @param {string} message The specific message to render as the reason for blocking
 * @param {string} dismissPath The location to "dismiss" to (otherwise just removes the modal)
 */
export const renderBlocker = ({ isDismissible = false, message, dismissPath }) => {

  // Remove any existing blocker. It's not likely (maybe not even possible) to
  // go from one blocked action/location to another within the same page load,
  // but best to be safe!
  removeBlocker();

  // Create the new modal element and attach it to the DOM
  const output = document.createElement("div");
  output.className = "non-active-subscription-backdrop";
  document.body.appendChild(output);

  /**
   * Get the school from the Context object in order to derive the display
   * price.
   *
   * It's currently the case that all callers of this method do so at a point
   * where the Context object should be populated, but we defensively handle
   * the case where it's not (`getBaseSubscriptionCost` will just return the
   * international price as a fallback).
   */
  const school = Context?.user?._school || null;
  const price = getBaseSubscriptionCost(school);

  // Populate the modal with our content
  output.innerHTML = `
    <div class="non-active-subscription-wrapper">
      <div class="non-active-subscription-content">

        <div class="non-active-subscription-message">

          <h2>Subscribe now for full access</h2>

          ${message ? `<p class="reason">&#9888; ${message}</p>` : ""}

          <div class="highlight">
            <h3><span>£${price.toString()}/year</span> per school</h3>
            <span>Price excludes any applicable taxes</span>
          </div>

          <div class="benefits-container">
            <div class="benefits">
              <div class="benefits-row benefits-header benefits-sticky">
                <div class="benefits-label">Advanced features</div>
                <div class="benefits-col-header">
                  <span>Subscribed</span>
                  <div class="benefits-badges">
                    <span class="benefit-badge badge-paid">Paid</span>
                  </div>
                </div>
                <div class="benefits-col-header">
                  <span>Limited</span>
                  <div class="benefits-badges">
                    <span class="benefit-badge badge-free">Free</span>
                  </div>
                </div>
              </div>
              <div class="benefits-row"><div class="benefits-label">Unlimited teacher accounts</div><div class="benefits-cell is-paid-check">${badgeCheckSVG}</div><div class="benefits-cell is-check">${checkSVG}</div></div>
              <div class="benefits-row"><div class="benefits-label">Create classes and add students</div><div class="benefits-cell is-paid-check">${badgeCheckSVG}</div><div class="benefits-cell is-lock">${limitedBlockerIcon}</div></div>
              <div class="benefits-row"><div class="benefits-label">Sync classes with your MIS</div><div class="benefits-cell is-paid-check">${badgeCheckSVG}</div><div class="benefits-cell is-lock">${limitedBlockerIcon}</div></div>
              <div class="benefits-row"><div class="benefits-label">Set online tasks &amp; homework</div><div class="benefits-cell is-paid-check">${badgeCheckSVG}</div><div class="benefits-cell is-lock">${limitedBlockerIcon}</div></div>
              <div class="benefits-row"><div class="benefits-label">Track student progress</div><div class="benefits-cell is-paid-check">${badgeCheckSVG}</div><div class="benefits-cell is-lock">${limitedBlockerIcon}</div></div>
              <div class="benefits-row"><div class="benefits-label">Give students feedback</div><div class="benefits-cell is-paid-check">${badgeCheckSVG}</div><div class="benefits-cell is-lock">${limitedBlockerIcon}</div></div>
              <div class="benefits-row"><div class="benefits-label">Create tailored worksheets</div><div class="benefits-cell is-paid-check">${badgeCheckSVG}</div><div class="benefits-cell is-lock">${limitedBlockerIcon}</div></div>
              <div class="benefits-row"><div class="benefits-label">Print any task as a worksheet</div><div class="benefits-cell is-paid-check">${badgeCheckSVG}</div><div class="benefits-cell is-lock">${limitedBlockerIcon}</div></div>
              <div class="benefits-row"><div class="benefits-label">Generate shadow papers</div><div class="benefits-cell is-paid-check">${badgeCheckSVG}</div><div class="benefits-cell is-lock">${limitedBlockerIcon}</div></div>
              <div class="benefits-row"><div class="benefits-label">Build custom courses</div><div class="benefits-cell is-paid-check">${badgeCheckSVG}</div><div class="benefits-cell is-lock">${limitedBlockerIcon}</div></div>
              <div class="benefits-row"><div class="benefits-label">Run Live! Interactive games</div><div class="benefits-cell is-paid-check">${badgeCheckSVG}</div><div class="benefits-cell is-lock">${limitedBlockerIcon}</div></div>
              <div class="benefits-row benefits-header">
                <div class="benefits-label">Lesson resources</div>
                <div></div>
                <div></div>
              </div>
              <div class="benefits-row"><div class="benefits-label">Lesson slides and printable exercises</div><div class="benefits-cell is-paid-check">${badgeCheckSVG}</div><div class="benefits-cell is-check">${checkSVG}</div></div>
              <div class="benefits-row"><div class="benefits-label">Pre-made courses (e.g. White Rose)</div><div class="benefits-cell is-paid-check">${badgeCheckSVG}</div><div class="benefits-cell is-check">${checkSVG}</div></div>
              <div class="benefits-row"><div class="benefits-label">Question generators, exam questions and past papers</div><div class="benefits-cell is-paid-check">${badgeCheckSVG}</div><div class="benefits-cell is-check">${checkSVG}</div></div>
              <div class="benefits-row"><div class="benefits-label">Worked example videos</div><div class="benefits-cell is-paid-check">${badgeCheckSVG}</div><div class="benefits-cell is-check">${checkSVG}</div></div>
            </div>
          </div>

          <a class="new-style-button new-style-button-primary" href="${subscriptionRoute}">Subscribe now</a>
          ${isDismissible
      ? `<button class="new-style-button new-style-button-simple" data-action="close">Continue with limited access</button>`
      : `<a class="new-style-button new-style-button-simple" href="/dashboard">Continue with limited access</a>`}

          <div class="scroll-buffer"></div>

        </div>

        <div class="non-active-subscription-illustration">
          <img src="/img/illustrations/platform-feature-snippets.svg">
        </div>

      </div>

      ${isDismissible ? `<button data-action="close" class="close-button"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M18 6 6 18"/><path d="M6 6l12 12"/></svg><span class="sr-only">Close</span></button>` : ""}
    </div>
  `;

  // Handle (optional) dismissal of this modal
  if (isDismissible) {
    const dismiss = () => {
      if (dismissPath) {
        window.location.href = dismissPath;
      } else {
        removeBlocker();
      }
    };

    output.addEventListener("click", (e) => {
      if (e.target === output) {
        dismiss();
      }
    });

    const closeButtons = output.querySelectorAll("[data-action='close']");
    for (const button of closeButtons) {
      button.addEventListener("click", () => {
        dismiss();
      });
    }
  }
}


/**
 * Removes the full-screen blocker modal, if present.
 */
const removeBlocker = () => {
  const existingBlocker = document.querySelector(".non-active-subscription-backdrop");
  if (existingBlocker) {
    document.body.removeChild(existingBlocker);
  }
}


/** A set of custom functions for deeper checks of certain URLs */
const customHandlers = {
  worksheetsHandler: (location) => {
    const urlParams = new URL(location).searchParams;

    // Worksheet templates are flat blocked for restricted users
    const wtid = urlParams.get("wtid");
    if (wtid !== null) {
      return "Your school needs an active subscription to use worksheet templates";
    }

    // The "New" worksheet screen is blocked for restricted users
    const wid = urlParams.get("wid");
    if (wid === "" || wid === "new") {
      return "Your school needs an active subscription to create and use custom worksheets";
    }

    // Since the page's contents are dynamic there's a lot of additional custom
    // handling within worksheets-newnew.js (loadWorksheet, loadDirectory, etc.)
    // which leverages the item's ancestors, and other information not available
    // here. So we let it through, to be blocked (if needed) after "hydration".
    return false;
  },
  progressHandler: (location) => {
    const urlParams = new URL(location).searchParams;
    const urlHash = new URL(location).hash;

    // Block leaderboard
    if (urlHash === "#tabs-leaderboards") {
      return "Your school needs an active subscription to enable leaderboards"
    }

    const mode = urlParams.get("mode");
    // The individual student views are allowed
    if (["studentprogress", "tasklist"].indexOf(mode) !== -1) {
      return false;
    }

    // We haven't been explicitly allowed to view this, so we'll block it.
    return "Your school needs an active subscription to view student progress";
  },
}


// Our main lookup object, keyed on absolute path, with a value of either plain
// message text (if the whole page is blocked), or a function returning either a
// suitable message if the current page should be blocked, or a literal `false`
// value if no blocking is needed. This function optionally takes a URL string,
// defaulting to the current window location.
//
// (Could also be used to decorate links to these paths, but unfortunately we do
// a lot of click-handler `location.href = "xxx"` rather than actual <a> links.)
const blockedPages = {
  "/settings.php": "Your school needs an active subscription to manage classes and students",
  "/edit-course.php": "Your school needs an active subscription to create and edit courses",
  "/manage-courses.php": "Your school needs an active subscription to manage courses",
  "/progress.php": customHandlers.progressHandler,
  "/worksheets.php": customHandlers.worksheetsHandler,
};


/**
 * Gets the nearest data-unsubscribed-message value for an element by traversing
 * the DOM, falling back to a default if none is found.
 */
const getRestrictedMessage = (DOMElement) => {
  let currentElement = DOMElement;
  while (currentElement !== document.body) {
    if (currentElement.dataset?.unsubscribedMessage) {
      return currentElement.dataset.unsubscribedMessage;
    }
    currentElement = currentElement.parentElement;
  }
  return "Your school needs an active subscription to access this feature";
};


/**
 * Inspects an element for "restricted" components, adding a title/tooltip and
 * a handler to show the blocker modal.
 *
 * This does NOT prevent any actual underlying functionality, since the handlers
 * are variously added by jQuery, inline attributes, and other code. Preventing
 * the actions should be handled by the specific implementations, usually by
 * leveraging `window.DFL.isUnsubscribedTeacher`.
 *
 * - The "restricted" class triggers the action on interactive child elements.
 * - "restricted-left" causes the lock icon to be placed *before* the content.
 * - "restricted-hide" causes the interactive elements to be removed. This can
 *     be applied either to individual elements, or a whole "restricted" block.
 * - "restricted-interactive" flags that an unexpected element should be
 *     considered as  "interactive" for the purpose of this code.
 */
const blockContent = (element) => {
  // Add the padlock icon to restricted UI elements, starting from either this
  // element if it's restricted directly, or its set of restricted children.
  const restrictedRoots = element.classList.contains(".restricted")
    ? [element]
    : [...element.querySelectorAll(".restricted")];

  restrictedRoots.forEach((restrictedRoot) => {
    // Bail out if we've already processed this element, otherwise mark it
    // as successfully processed (technically a bit early, but never mind.)
    if (restrictedRoot.dataset.restrictedProcessed) { return; }
    restrictedRoot.dataset.restrictedProcessed = true;

    // If this "root" is itself one of our interactive elements types, we'll
    // operate on it directly, otherwise we collect its interactive children,
    // with the assumption that <button>, <a>, and misc. inputs are the targets!
    // NOTE: This does *not* cover all interactive elements on the legacy site,
    //       since there are click handlers sprinkled on <div>, <span>, etc.
    //       In those cases the "restricted-interactive" class can be used to
    //       indicate that the element needs to be included in this processing.
    const rootIsInteractive =
      restrictedRoot.classList.contains("restricted-interactive")
      || ["A", "BUTTON", "INPUT", "SELECT"].indexOf(restrictedRoot.tagName) !== -1;
    const restrictedUIElements = rootIsInteractive
      ? [restrictedRoot]
      : [...restrictedRoot.querySelectorAll("a, button, input, select, .restricted-interactive")];

    restrictedUIElements.forEach((restrictedUIElement) => {
      // If we're instructed to hide the UI element, apply a direct style and
      // return. We use setAttribute() instead of element.style.display so
      // that we can use !important here.
      if (
        restrictedRoot.classList.contains("restricted-hide")
        || restrictedUIElement.classList.contains("restricted-hide")
      ) {
        restrictedUIElement.setAttribute("style", "display: none !important");
        return;
      }

      // Get the most-specific message available to this element
      const restrictedMessage = getRestrictedMessage(restrictedUIElement);

      // Set the title text (for showing on hover/assistive tech)
      restrictedUIElement.setAttribute("title", restrictedMessage);

      // Create our visual indicator element (just an empty SVG for now)
      const padlockImg = document.createElementNS("http://www.w3.org/2000/svg", "svg");

      // Append this indicator, noting that sometimes the padlock icon belongs
      // in *front* of the existing content (e.g., the top-right user menu)
      if (restrictedRoot.classList.contains("restricted-left")) {
        restrictedUIElement.insertBefore(padlockImg, restrictedUIElement.firstChild);
      } else {
        restrictedUIElement.appendChild(padlockImg);
      }

      // Update with the full SVG content (possible now it's within the DOM)
      padlockImg.outerHTML = limitedBlockerIcon;

      // Handle clicks by showing the blocker modal
      // NOTE: preventDefault() prevents basic link navigation, but does NOT
      //       prevent other event listeners that may be present from firing,
      //       which is the responsibility of those specific target functions.
      restrictedUIElement.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        renderBlocker({ isDismissible: true, message: restrictedMessage });
      });
    });
  });
};

// Support bits for our MutationObserver.
// This also allows us to indirectly "observe" URL changes, by comparing with
// a stored value whenever there's an on-page mutation. (Since these things are
// fairly closely coupled ― a mutation is PROBABLY due to a location
// update, or the location update is the result of the mutation.)
let previousLocation = document.location.href;
const callback = (mutationList) => {
  // Check for location updates, since we might now need a full-page blocker.
  if (previousLocation !== document.location.href) {
    previousLocation = document.location.href;
    loadAction();
  }

  // Pass all changed DOM content over to the content blocker function.
  for (const mutation of mutationList) {
    blockContent(mutation.target);
  }
};


// Set things up, and start the MutationObserver
const start = () => {
  // Immediately block the whole screen if needed
  loadAction();

  // Immediately block anything already in the DOM (Though it usually won't be
  // much at this point, since so much stuff is loaded/generated dynamically.)
  blockContent(document.body);

  // Set up our mutationObserver to intercept any future elements that get added
  // to the DOM. Sigh.
  const mutationObserver = new MutationObserver(callback);
  mutationObserver.observe(document.body, { childList: true, subtree: true });
};

export const init = () => {
  // Only apply limited experience to teacher-like users at unsubscribed schools.
  if (!window.DFL?.isUnsubscribedTeacher) { return; }

  // Add a body class so that our visual styles apply to blocked content
  document.body.classList.add("non-active-subscription");

  // Immediately block the whole screen if needed, otherwise block things at a
  // component level and set up the mutationObserver. Note that we'll only take
  // action *after* the page has loaded in, since we need a `body` element to
  // operate upon!
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", loadAction);
    document.addEventListener("DOMContentLoaded", start);
  } else {
    loadAction();
    start();
  }
};
